import Header from "@components/Header/Header"
import MerchantProfilePicture from "@components/MerchantProfilePicture/MerchantProfilePicture"
import PrimaryButton from "@components/PrimaryButton/PrimaryButton"
import TextInput from "@components/TextInput/TextInput"
import { useLocation, useNavigate } from "react-router"
import {
    Container,
    ContentContainer,
    MerchantInfoCard,
    OptionalText,
    Divider,
    ButtonContainer,
    EmailWarningText
} from "./MerchantInformation.styles"
import { useState, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import useManageMerchant from "@hooks/useManageMerchant"
import LoadingModal from "@components/LoadingModal/LoadingModal"
import SuccessModal from "@components/SuccessModal/SuccessModal"
import Snackbar from "@components/Snackbar/Snackbar"
import * as Sentry from "@sentry/react"
import { setHeaderColor } from "@lib/telegram"
import { WHITE } from "@styles/colors"
import { ApiError } from "@lib/api"

const SCROLL_OFFSET_MERCHANT_NAME = 200;
const SCROLL_OFFSET_EMAIL = 300;
const FOCUS_SCROLL_DELAY = 500;

const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

const validateMerchantName = (merchantName: string) => {
    return merchantName.trim().length > 0
}

const MerchantInformation = () => {
    const { state } = useLocation()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const {
        loading,
        uploadMerchantImage,
        updateMerchantInfo,
        createMerchant,
        merchantInformation
    } = useManageMerchant()
    const containerRef = useRef<HTMLDivElement>(null)

    const [merchantLocalInfo, setMerchantLocalInfo] = useState({
        email: merchantInformation?.email || state.email || "",
        merchantName: merchantInformation?.name || "",
        localProfilePictureUrl: merchantInformation?.profilePictureUrl || "",
        isEmailValid: true,
        isMerchantNameValid: true
    })

    const [isChangeMade, setIsChangeMade] = useState(false)
    const [successOpen, setSuccessOpen] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        setIsChangeMade(
            merchantLocalInfo.email !== merchantInformation?.email ||
            merchantLocalInfo.merchantName !== merchantInformation?.name ||
            merchantLocalInfo.localProfilePictureUrl !== merchantInformation?.profilePictureUrl
        )
    }, [merchantLocalInfo.email, merchantLocalInfo.merchantName, merchantLocalInfo.localProfilePictureUrl, merchantInformation])

    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleSubmit = async () => {
        try {

            if (!merchantInformation?.name) {
                await createMerchant({
                    business_name: merchantLocalInfo.merchantName,
                    email: merchantLocalInfo.email
                })
                setSuccessMessage(t('authentication.successMessages.addedSuccessfully'))
            } else {
                // Update merchant info
                await updateMerchantInfo({
                    business_name: merchantLocalInfo.merchantName,
                    email: merchantLocalInfo.email
                })
                setSuccessMessage(t('authentication.successMessages.updatedSuccessfully'))
            }


            // If there's a profile picture URL and it has changed, upload it
            if (merchantLocalInfo.localProfilePictureUrl !== merchantInformation?.profilePictureUrl && isChangeMade) {
                // Pass the base64 image data directly
                await uploadMerchantImage(merchantLocalInfo.localProfilePictureUrl)
            }

            // Show success modal
            setSuccessOpen(true)

            // Navigate to payment link page after delay
            navigationTimeoutRef.current = setTimeout(() => {
                navigate('/payment-link')
            }, 2000)
        } catch (error: unknown) {
            Sentry.captureException(error)
            if (error instanceof ApiError) {
                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)
                const errorMsg = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')
                setErrorMessage(errorMsg)
            } else {
                setErrorMessage(t('errors.genericServerDescription'))
            }
        }
    }

    const handleMerchantNameFocus = () => {
        setTimeout(() => {
            if (containerRef.current) {
                const startPosition = containerRef.current.scrollTop;
                const targetPosition = SCROLL_OFFSET_MERCHANT_NAME;
                const duration = 100; // milliseconds - adjust for faster/slower animation
                const startTime = performance.now();

                const animateScroll = (currentTime: number) => {
                    const elapsedTime = currentTime - startTime;
                    const progress = Math.min(elapsedTime / duration, 1);
                    // Use easeOutQuad easing function for a natural feel
                    const easeProgress = 1 - (1 - progress) * (1 - progress);

                    containerRef.current!.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        }, FOCUS_SCROLL_DELAY)
    }

    const handleEmailFocus = () => {
        setTimeout(() => {
            if (containerRef.current) {
                const startPosition = containerRef.current.scrollTop;
                const targetPosition = SCROLL_OFFSET_EMAIL;
                const duration = 100; // milliseconds - adjust for faster/slower animation
                const startTime = performance.now();

                const animateScroll = (currentTime: number) => {
                    const elapsedTime = currentTime - startTime;
                    const progress = Math.min(elapsedTime / duration, 1);
                    // Use easeOutQuad easing function for a natural feel
                    const easeProgress = 1 - (1 - progress) * (1 - progress);

                    containerRef.current!.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        }, FOCUS_SCROLL_DELAY)
    }

    useEffect(() => {
        setHeaderColor(WHITE)
    }, [])

    useEffect(() => {
        return () => {
            if (navigationTimeoutRef.current) {
                clearTimeout(navigationTimeoutRef.current)
            }
        }
    }, [])

    return (
        <Container>
            <Header title={t('merchantInformation.header')} showBackButton onBackClick={() => navigate('/payment-link')} />
            <LoadingModal visible={loading} />
            {successOpen && <SuccessModal
                visible={successOpen}
                message={successMessage}
                onHide={() => setSuccessOpen(false)}
            />}
            <ContentContainer ref={containerRef}>
                <MerchantInfoCard>
                    <MerchantProfilePicture
                        url={merchantLocalInfo.localProfilePictureUrl}
                        onClick={() => fileInputRef.current?.click()}
                    />
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                    const imageUrl = event.target?.result as string;
                                    setMerchantLocalInfo({
                                        ...merchantLocalInfo,
                                        localProfilePictureUrl: imageUrl
                                    });
                                };
                                reader.readAsDataURL(file);
                            }
                        }}
                    />
                    <OptionalText>
                        {t('merchantInformation.profilePicture.optional')}
                    </OptionalText>
                    <Divider />
                    <TextInput
                        title={t('merchantInformation.form.merchantName')}
                        value={merchantLocalInfo.merchantName}
                        onBlur={(e) => setMerchantLocalInfo(prev => ({
                            ...prev,
                            isMerchantNameValid: validateMerchantName(e.target.value)
                        }))}
                        isError={!merchantLocalInfo.isMerchantNameValid && isChangeMade}
                        errorText={t('merchantInformation.form.nameRequired')}
                        onChange={(e) => setMerchantLocalInfo({ ...merchantLocalInfo, merchantName: e.target.value })}
                        onFocus={handleMerchantNameFocus}
                    />
                    <TextInput
                        title={t('merchantInformation.form.email')}
                        value={merchantLocalInfo.email}
                        onBlur={(e) => setMerchantLocalInfo(prev => ({
                            ...prev,
                            isEmailValid: validateEmail(e.target.value)
                        }))}
                        isError={!merchantLocalInfo.isEmailValid && isChangeMade}
                        errorText={'Invalid email'}
                        onChange={(e) => setMerchantLocalInfo({ ...merchantLocalInfo, email: e.target.value })}
                        onFocus={handleEmailFocus}
                    />
                    <EmailWarningText>
                        {t('merchantInformation.emailWarning')}
                    </EmailWarningText>
                </MerchantInfoCard>
                <ButtonContainer>
                    <PrimaryButton
                        disabled={
                            !isChangeMade ||
                            !merchantLocalInfo.merchantName ||
                            !merchantLocalInfo.email ||
                            !merchantLocalInfo.isEmailValid
                        }
                        onClick={handleSubmit}
                    >
                        {t('merchantInformation.form.submitButton')}
                    </PrimaryButton>
                </ButtonContainer>
            </ContentContainer>
            {errorMessage && <Snackbar
                width='90%'
                duration={8000}
                message={errorMessage}
                isVisible={!!errorMessage}
                onClose={() => setErrorMessage('')}
            />}
        </Container>
    )
}

export default MerchantInformation